import { useSelector } from 'react-redux';

import {
  MANUFACTURER_ID_AYLA,
  MANUFACTURER_ID_SATURN,
  SENSE_MONITOR_MANUFACTURERS,
} from '../../features/devices/devices-helper';
import { useGetSwitchesByManufacturerQuery } from '../../features/devices/switches/switchApi';
import {
  selectBatteries,
  selectCellularModems,
  selectEvChargers,
  selectInverters,
  selectMeters,
  selectSite,
} from '../../features/site/siteSlice';

/**
 * Custom hook to check the capabilities of the site based on the devices available and site details.
 *
 * @returns Site capabilities to inform various UI components in the app
 */
export function useSiteCapabilityCheck(): {
  siteHasEvCharger: boolean;
  siteHasOnlyEvCharger: boolean;
  siteHasMeter: boolean;
  siteHasConnectedMeter: boolean;
  siteHasInverter: boolean;
  siteHasBattery: boolean;
  siteHasAylaSwitches: boolean;
  siteHasInverterSolar: boolean;
  siteHasMeterSolar: boolean;
  isLoading: boolean;
  isFetching: boolean;
} {
  const {
    data: aylaSwitchesFromApi,
    isLoading,
    isFetching,
  } = useGetSwitchesByManufacturerQuery([MANUFACTURER_ID_AYLA]);

  const { has_solar: hasSolar, is_consumption_site: isConsumptionSite } = useSelector(selectSite);
  const siteInverters = useSelector(selectInverters);
  const siteMeters = useSelector(selectMeters);
  const siteCellularModems = useSelector(selectCellularModems);

  const siteHasEvCharger = useSelector(selectEvChargers).length > 0;
  const siteHasBattery = useSelector(selectBatteries).length > 0;
  // SE Home only supports Sense/Schneider Meters
  const siteCompatibleMeters = siteMeters.filter((meter) =>
    SENSE_MONITOR_MANUFACTURERS.includes(meter.manufacturer_id)
  );
  const siteHasMeter = siteCompatibleMeters.length > 0;
  // We need to differentiate between connected and not connected meters, to support different UI components --
  // Some features are not available when the meter is not connected ONLY when the site has a cellular modem
  const siteHasConnectedMeter =
    siteHasMeter &&
    (siteCellularModems.length > 0
      ? siteCompatibleMeters.some((meter) => meter.operating_state !== 'NOT_CONNECTED')
      : true);

  // SE Home only supports Saturn inverters
  const siteHasInverter = siteInverters.some((inverter) => [MANUFACTURER_ID_SATURN].includes(inverter.manufacturer_id));
  const inverterHasSolar = siteHasInverter && (hasSolar ?? true);

  // We need to differentiate between solar via the inverter or via the meter, to support different UI components
  const siteHasInverterSolar = inverterHasSolar;
  const siteHasMeterSolar = siteHasMeter && !siteHasInverter && !isConsumptionSite;
  const siteHasAylaSwitches = aylaSwitchesFromApi?.length > 0;

  const siteHasOnlyEvCharger =
    siteHasEvCharger &&
    siteHasBattery === false &&
    siteHasInverter === false &&
    siteHasMeter === false &&
    siteHasConnectedMeter === false &&
    siteHasAylaSwitches === false &&
    siteHasInverterSolar === false &&
    siteHasMeterSolar === false;

  return {
    siteHasEvCharger,
    siteHasOnlyEvCharger,
    siteHasBattery,
    siteHasInverter,
    siteHasMeter,
    siteHasConnectedMeter,
    siteHasAylaSwitches,
    siteHasInverterSolar,
    siteHasMeterSolar,
    isLoading,
    isFetching,
  };
}
